import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoSectionResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface CreateSectionInput {
  name: string;
  description?: string;
  parentSectionId?: string | null;
  sectionType?: 'timeframe' | 'category';
  sortOrder?: number;
}

interface UpdateSectionInput {
  name?: string;
  description?: string | null;
  sortOrder?: number;
  sectionType?: 'timeframe' | 'category';
  parentSectionId?: string | null;
  hiddenFromPublic?: boolean;
}

interface UseTodoSectionsReturn {
  sections: TodoSectionResponse[];
  loading: boolean;
  refresh: () => void;
  createSection: (input: CreateSectionInput) => Promise<TodoSectionResponse>;
  updateSection: (vars: {
    sectionId: string;
    input: UpdateSectionInput;
  }) => Promise<TodoSectionResponse>;
  deleteSection: (sectionId: string) => Promise<void>;
}

/**
 * Sections live behind their own GET so a section mutation only
 * invalidates `sections(holder, list)` — not the list row itself.
 * Reorder is folded into `updateSection({sortOrder})` per the new
 * substrate verb set; an explicit bulk reorder endpoint is a follow-up.
 */
export function useTodoSections(holderId: string | null, listId: string): UseTodoSectionsReturn {
  const queryClient = useQueryClient();
  const sectionsKey = queryKeys.todoLists.sections(holderId ?? '', listId);
  const { toast } = useToast();

  const onError = () => {
    toast({ title: 'Something went wrong', variant: 'destructive' });
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: sectionsKey,
    queryFn: () => api.listTodoSections(holderId as string, listId),
    enabled: !!holderId && !!listId,
  });

  const createMut = useMutation({
    mutationFn: (input: CreateSectionInput) =>
      api.createTodoSection(holderId as string, { listId, ...input }).then((r) => r.section),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sectionsKey });
    },
    onError,
  });

  const updateMut = useMutation({
    mutationFn: ({ sectionId, input }: { sectionId: string; input: UpdateSectionInput }) =>
      api.updateTodoSection(holderId as string, sectionId, input).then((r) => r.section),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sectionsKey });
    },
    onError,
  });

  const deleteMut = useMutation({
    mutationFn: (sectionId: string) => api.deleteTodoSection(holderId as string, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sectionsKey });
    },
    onError,
  });

  return {
    sections: data?.sections ?? [],
    loading: isLoading,
    refresh: () => {
      refetch();
    },
    createSection: (input) => createMut.mutateAsync(input),
    updateSection: (vars) => updateMut.mutateAsync(vars),
    deleteSection: (sectionId) => deleteMut.mutateAsync(sectionId),
  };
}
